// src/pages/ProjectOracleV2.jsx
// Route: /project-oracle-v2
// Axiom Intelligence — Owner's Project Flow (V2 Design Rebuild)
// V2: Dark canvas · structural rails · timeline spine · risk-propagating arrows
// Original preserved at ProjectOracle.jsx — do not modify that file.
// Layer 1: Portfolio view (all sites, phase health)
// Layer 2: Site view (IDEF0 A1-A5 phase flow, live CORR-004 colors)
// Layer 3: Activity drilldown (Phase 2)

import React, { useState, useEffect, useCallback } from 'react';
import PhaseOracle from './PhaseOracle';

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_URL || '';

const SITES = [
  { code: 'DALLAS',      name: 'Dallas',       city: 'Dallas / Plano, TX',        state: 'TX', iso: 'ERCOT', region: 'US'    },
  { code: 'ASHBURN',     name: 'Ashburn',      city: 'Ashburn / Sterling, VA',    state: 'VA', iso: 'PJM',   region: 'US'    },
  { code: 'PHOENIX',     name: 'Phoenix',      city: 'Phoenix, AZ',               state: 'AZ', iso: 'WECC',  region: 'US'    },
  { code: 'CHICAGO',     name: 'Chicago',      city: 'Northlake, IL',             state: 'IL', iso: 'MISO',  region: 'US'    },
  { code: 'SALTLAKE',    name: 'Salt Lake',    city: 'West Jordan, UT',           state: 'UT', iso: 'WECC',  region: 'US'    },
  { code: 'FREDERICK',   name: 'Frederick',    city: 'Frederick, MD',             state: 'MD', iso: 'PJM',   region: 'US'    },
  { code: 'PORTLAND',    name: 'Portland',     city: 'Portland, OR',              state: 'OR', iso: 'WECC',  region: 'US'    },
  { code: 'SAOPAULO',    name: 'Sao Paulo',    city: 'Sao Paulo, Brazil',         state: null, iso: null,    region: 'LATAM' },
  { code: 'BOGOTA',      name: 'Bogota',       city: 'Bogota, Colombia',          state: null, iso: null,    region: 'LATAM' },
  { code: 'QUERETARO',   name: 'Queretaro',    city: 'Queretaro, Mexico',         state: null, iso: null,    region: 'LATAM' },
  { code: 'SANTIAGO',    name: 'Santiago',     city: 'Santiago, Chile',           state: null, iso: null,    region: 'LATAM' },
  { code: 'BUENOSAIRES', name: 'Buenos Aires', city: 'Buenos Aires, Argentina',   state: null, iso: null,    region: 'LATAM' },
  { code: 'LIMA',        name: 'Lima',         city: 'Lima, Peru',                state: null, iso: null,    region: 'LATAM' },
];

const SITE_META = {
  FREDERICK:  { iso: 'PJM'   },
  ASHBURN:    { iso: 'PJM'   },
  DALLAS:     { iso: 'ERCOT' },
  CHICAGO:    { iso: 'MISO'  },
  PHOENIX:    { iso: 'WECC'  },
  SALTLAKE:   { iso: 'WECC'  },
  PORTLAND:   { iso: 'WECC'  },
};

// IDEF0 Phase definitions
const PHASES = [
  {
    id: 'A1',
    label: 'Pre-Development',
    sub: 'Month 1-6',
    icon: '[A1]',
    activities: ['Site Evaluation & Selection', 'Project Feasibility & Pro Forma', 'Program Development + Team Assembly'],
    corr004Keys: ['queue_delay', 'site_risk'],
    description: 'Site selection, feasibility, program definition',
  },
  {
    id: 'A2',
    label: 'Design',
    sub: 'Month 3-12',
    icon: '[A2]',
    activities: ['Design Management + Value Engineering', 'Long-Lead Procurement + Coordination'],
    corr004Keys: ['procurement_risk'],
    description: 'Design management and long-lead procurement',
  },
  {
    id: 'A3',
    label: 'Construction',
    sub: 'Month 9-24',
    icon: '[A3]',
    activities: ['Schedule & Cost Management', 'Quality & Vendor Management'],
    corr004Keys: ['queue_delay', 'procurement_risk'],
    description: 'Schedule, cost, quality and vendor management',
  },
  {
    id: 'A4',
    label: 'Commissioning',
    sub: 'Month 18-26',
    icon: '[A4]',
    activities: ['Plan Review + Functional Performance Testing', 'Tenant Acceptance Testing'],
    corr004Keys: ['site_risk'],
    description: 'Commissioning and tenant acceptance',
  },
  {
    id: 'A5',
    label: 'Turnover',
    sub: 'Month 24+',
    icon: '[A5]',
    activities: ['Close-Out + Operations Readiness', 'Warranty Period Management'],
    corr004Keys: [],
    description: 'Close-out and post-occupancy operations',
  },
];

// ─────────────────────────────────────────────────────────────
// UTILITIES
// ─────────────────────────────────────────────────────────────

const getPhaseScore = (phaseCorr004Keys, siteCorr004) => {
  if (!siteCorr004 || phaseCorr004Keys.length === 0) return null;
  const scores = phaseCorr004Keys
    .map(k => siteCorr004[k]?.score)
    .filter(s => s !== null && s !== undefined);
  if (scores.length === 0) return null;
  return Math.max(...scores);
};

const scoreToColor = (score) => {
  if (score === null || score === undefined) return {
    bg: '#f8fafc', border: '#cbd5e1', text: '#64748b',
    label: 'No Data', dot: '#94a3b8', glow: 'none',
  };
  if (score >= 75) return {
    bg: '#fef2f2', border: '#f87171', text: '#dc2626',
    label: 'High Risk', dot: '#ef4444', glow: '0 0 12px rgba(239,68,68,0.3)',
  };
  if (score >= 50) return {
    bg: '#fffbeb', border: '#fbbf24', text: '#d97706',
    label: 'Moderate', dot: '#f59e0b', glow: '0 0 12px rgba(245,158,11,0.3)',
  };
  if (score >= 25) return {
    bg: '#fefce8', border: '#facc15', text: '#ca8a04',
    label: 'Low-Mod', dot: '#eab308', glow: 'none',
  };
  return {
    bg: '#f0fdf4', border: '#4ade80', text: '#16a34a',
    label: 'Low Risk', dot: '#22c55e', glow: 'none',
  };
};

const getSiteOverallScore = (siteCorr004) => {
  if (!siteCorr004) return null;
  const scores = ['queue_delay', 'site_risk', 'procurement_risk']
    .map(k => siteCorr004[k]?.score)
    .filter(s => s !== null && s !== undefined);
  if (scores.length === 0) return null;
  return Math.max(...scores);
};

// ─────────────────────────────────────────────────────────────
// CUSTOM REACT FLOW NODES
// ─────────────────────────────────────────────────────────────




// ════════════════════════════════════════════════════════════════════════════
// V2 SITE FLOW — DARK CANVAS REDESIGN
// Controls rail · phase cards · inter-phase risk arrows · timeline spine
// Mechanisms rail · CORR-004 intelligence panel
// ════════════════════════════════════════════════════════════════════════════

// ── Layout constants (SVG viewBox: 0 0 1000 SVG_H) ──────────────────────────
const PL = [
  { x: 22,  cx: 110 },
  { x: 210, cx: 298 },
  { x: 398, cx: 486 },
  { x: 586, cx: 674 },
  { x: 774, cx: 862 },
];
const PH_W    = 176;
const PH_Y    = 90;
const PH_H    = 238;
const CTRL_H  = 82;
const TLINE_Y = PH_Y + PH_H + 12;
const TLINE_H = 44;
const MECH_Y  = TLINE_Y + TLINE_H + 8;
const MECH_H  = 62;
const SVG_H   = MECH_Y + MECH_H + 14;

const scoreCol = (score) => {
  if (score === null || score === undefined) return '#475569';
  if (score >= 75) return '#ef4444';
  if (score >= 50) return '#f59e0b';
  if (score >= 25) return '#eab308';
  return '#22c55e';
};

// ── Arc Gauge (SVG fragment) ─────────────────────────────────────────────────
const ArcGaugeSVG = ({ cx, cy, score, color, r = 14 }) => {
  const arc  = Math.PI * r;
  const fill = score !== null ? Math.min((score / 100) * arc, arc) : 0;
  const x0 = cx - r, x1 = cx + r;
  return (
    <>
      <path d={`M${x0} ${cy} A${r} ${r} 0 0 1 ${x1} ${cy}`}
            fill="none" stroke="#1e293b" strokeWidth={3.5} strokeLinecap="round" />
      {fill > 0 && (
        <path d={`M${x0} ${cy} A${r} ${r} 0 0 1 ${x1} ${cy}`}
              fill="none" stroke={color} strokeWidth={3.5} strokeLinecap="round"
              strokeDasharray={`${fill.toFixed(1)} ${arc.toFixed(1)}`} />
      )}
      <text x={cx} y={cy + 13} textAnchor="middle"
            fill={color} fontSize="8" fontWeight="700">
        {score !== null ? Math.round(score) : '\u2014'}
      </text>
    </>
  );
};

// ── Phase Card (SVG group) ───────────────────────────────────────────────────
const V2PhaseCard = ({ phase, pl, score, colors, onClick }) => {
  const { x } = pl;
  const y = PH_Y, w = PH_W, h = PH_H;
  const c     = colors.dot;
  const isHi  = score !== null && score >= 75;
  const isMed = score !== null && score >= 50;

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      {isHi && (
        <rect x={x - 3} y={y - 3} width={w + 6} height={h + 6} rx={13}
              fill="none" stroke={c} strokeWidth="1.5" opacity="0.2"
              style={{ filter: 'blur(7px)' }} />
      )}
      <rect x={x} y={y} width={w} height={h} rx={11}
            fill="#0d1829" stroke={c}
            strokeWidth={isMed ? 2 : 1} />
      <rect x={x + 1} y={y + 1} width={w - 2} height={36} rx={10}
            fill={`${c}18`} />
      <rect x={x + 1} y={y + 27} width={w - 2} height={10}
            fill={`${c}18`} />

      {/* Phase ID badge */}
      <rect x={x + 8} y={y + 8} width={24} height={17} rx={4}
            fill={`${c}28`} />
      <text x={x + 20} y={y + 21} textAnchor="middle"
            fill={c} fontSize="9" fontWeight="800">{phase.id}</text>

      {/* Month sub-label */}
      <text x={x + 38} y={y + 20} fill="#475569" fontSize="7.5">{phase.sub}</text>

      {/* Title */}
      <text x={x + 10} y={y + 54} fill="#e2e8f0" fontSize="11" fontWeight="700">
        {phase.label}
      </text>

      {/* Score + label */}
      <text x={x + 10} y={y + 69} fill={c} fontSize="9" fontWeight="700">
        {score !== null ? `${Math.round(score)}/100` : 'No Data'}
      </text>
      <text x={x + 10} y={y + 81} fill={c} fontSize="7.5" opacity="0.75">
        {colors.label}
      </text>

      {/* Arc gauge */}
      <ArcGaugeSVG cx={x + w - 24} cy={y + 64} score={score} color={c} r={14} />

      {/* Divider */}
      <line x1={x + 10} y1={y + 92} x2={x + w - 10} y2={y + 92}
            stroke="#1e3a5f" strokeWidth="0.75" />

      {/* Activities */}
      {phase.activities.slice(0, 3).map((act, i) => {
        const ay = y + 108 + i * 20;
        const lbl = act.length > 26 ? act.slice(0, 25) + '\u2026' : act;
        return (
          <g key={i}>
            <circle cx={x + 14} cy={ay - 4} r={2.5} fill={c} opacity="0.5" />
            <text x={x + 22} y={ay} fill="#94a3b8" fontSize="7.5">{lbl}</text>
          </g>
        );
      })}

      {/* Footer */}
      <line x1={x + 10} y1={y + h - 22} x2={x + w - 10} y2={y + h - 22}
            stroke="#1e3a5f" strokeWidth="0.5" />
      <text x={x + w / 2} y={y + h - 8} textAnchor="middle"
            fill={c} fontSize="7.5" fontWeight="600" opacity="0.9">
        Drill down {'\u2193'}
      </text>
    </g>
  );
};

// ── Timeline Spine ───────────────────────────────────────────────────────────
const V2Timeline = ({ scores }) => {
  const y = TLINE_Y, h = TLINE_H;
  const maxM = 36, W = 956, xOff = 22;
  const mX = (m) => xOff + ((m - 1) / (maxM - 1)) * W;
  const SPANS = [
    { s: 1,  e: 6,  pi: 0 },
    { s: 3,  e: 12, pi: 1 },
    { s: 9,  e: 24, pi: 2 },
    { s: 18, e: 26, pi: 3 },
    { s: 24, e: 36, pi: 4 },
  ];
  const NOW_M = 7;
  const nowX = mX(NOW_M);

  return (
    <>
      <rect x={xOff} y={y} width={W} height={h} rx={6}
            fill="#0a1428" stroke="#1e3a5f" strokeWidth="0.75" />
      <text x={xOff + 8} y={y + 14} fill="#334155" fontSize="7" fontWeight="700">
        PROJECT TIMELINE
      </text>
      {SPANS.map(({ s, e, pi }) => {
        const x1 = mX(s), x2 = mX(e);
        const c = scoreCol(scores[pi]);
        return (
          <g key={pi}>
            <rect x={x1} y={y + 18} width={x2 - x1} height={h - 20} rx={3}
                  fill={`${c}28`} stroke={c} strokeWidth="0.75" />
            <text x={(x1 + x2) / 2} y={y + 30} textAnchor="middle"
                  fill={c} fontSize="7" fontWeight="700">
              {PHASES[pi] && PHASES[pi].id}
            </text>
          </g>
        );
      })}
      {[1, 6, 12, 18, 24, 30, 36].map(m => {
        const mx = mX(m);
        return (
          <g key={m}>
            <line x1={mx} y1={y + h - 5} x2={mx} y2={y + h}
                  stroke="#334155" strokeWidth="0.75" />
            <text x={mx} y={y + h + 9} textAnchor="middle"
                  fill="#334155" fontSize="6">M{m}</text>
          </g>
        );
      })}
      <line x1={nowX} y1={y - 4} x2={nowX} y2={y + h + 4}
            stroke="#e2e8f0" strokeWidth="1.5"
            style={{ filter: 'drop-shadow(0 0 4px #e2e8f0)' }} />
      <circle cx={nowX} cy={y - 4} r={3} fill="#e2e8f0" />
      <text x={nowX + 5} y={y - 6} fill="#e2e8f0" fontSize="6" fontWeight="700">NOW</text>
    </>
  );
};

// ── V2 Intelligence Rail ─────────────────────────────────────────────────────
const V2IntelRail = ({ site, siteC4, scores }) => (
  <div style={{
    width: 220, background: '#0a0f1e',
    borderLeft: '1px solid #1e3a5f',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden', flexShrink: 0,
  }}>
    <div style={{
      padding: '14px 16px 10px',
      borderBottom: '1px solid #1e3a5f',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <div style={{
        width: 7, height: 7, borderRadius: '50%',
        background: '#22c55e', boxShadow: '0 0 6px #22c55e',
      }} />
      <span style={{
        fontSize: 9, fontWeight: 700, color: '#38bdf8',
        textTransform: 'uppercase', letterSpacing: '0.12em',
      }}>CORR-004 Intelligence</span>
    </div>

    <div style={{
      flex: 1, overflowY: 'auto', padding: '12px 14px',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      {[
        { key: 'queue_delay',      label: 'Queue Delay'      },
        { key: 'site_risk',        label: 'Site Risk'        },
        { key: 'procurement_risk', label: 'Procurement Risk' },
      ].map(({ key, label }) => {
        const row = siteC4 && siteC4[key];
        const c = scoreToColor(row ? row.score : null);
        return (
          <div key={key} style={{
            background: '#111827',
            border: `1px solid ${c.dot}35`,
            borderLeft: `3px solid ${c.dot}`,
            borderRadius: 8, padding: '10px 12px',
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 4,
            }}>
              <span style={{ fontSize: 9, color: '#64748b', fontWeight: 600 }}>
                {label}
              </span>
              <span style={{ fontSize: 16, fontWeight: 800, color: c.dot }}>
                {row ? Math.round(row.score) : '\u2014'}
              </span>
            </div>
            {row && row.summary ? (
              <div style={{ fontSize: 8.5, color: '#94a3b8', lineHeight: 1.5 }}>
                {row.summary.length > 90 ? row.summary.slice(0, 89) + '\u2026' : row.summary}
              </div>
            ) : (
              <div style={{ fontSize: 8.5, color: '#334155', fontStyle: 'italic' }}>
                No data {'\u2014'} run CORR-004
              </div>
            )}
          </div>
        );
      })}

      <div style={{
        background: '#0d1829', border: '1px solid #1e3a5f',
        borderRadius: 8, padding: '10px 12px',
      }}>
        <div style={{
          fontSize: 8.5, fontWeight: 700, color: '#38bdf8',
          marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>Phase Risk Summary</div>
        {PHASES.map((phase, i) => {
          const sc = scores[i];
          const c = scoreToColor(sc);
          return (
            <div key={phase.id} style={{
              display: 'flex', alignItems: 'center',
              justifyContent: 'space-between',
              padding: '4px 0', borderBottom: '1px solid #1e293b',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: c.dot, flexShrink: 0,
                }} />
                <span style={{ fontSize: 8.5, color: '#94a3b8' }}>
                  {phase.id} {'\u00B7'} {phase.label}
                </span>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, color: c.dot }}>
                {sc !== null ? Math.round(sc) : '\u2014'}
              </span>
            </div>
          );
        })}
      </div>

      <div style={{
        background: '#0a0f1a', border: '1px solid #1e3a5f',
        borderRadius: 8, padding: '10px 12px',
      }}>
        <div style={{
          fontSize: 8.5, fontWeight: 700, color: '#475569',
          marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>Site Data Sources</div>
        {[
          'LBNL Queue \u00B7 36K+ records',
          'EIA Grid Capacity',
          'NOAA Climate Events',
          'BLS Construction Labor',
        ].map((src, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center',
            gap: 6, padding: '3px 0',
          }}>
            <div style={{
              width: 5, height: 5, borderRadius: '50%',
              background: '#1e3a5f', flexShrink: 0,
            }} />
            <span style={{ fontSize: 8, color: '#334155' }}>{src}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ── SiteFlowV2 — Main redesigned site view ───────────────────────────────────
const SiteFlowV2 = ({ site, corr004, onPhaseClick, onBack, onSiteChange }) => {
  const siteC4  = corr004[site.code] || null;
  const iso     = (SITE_META[site.code] && SITE_META[site.code].iso) || site.iso || '\u2014';
  const scores  = PHASES.map(ph => getPhaseScore(ph.corr004Keys, siteC4));
  const overall = getSiteOverallScore(siteC4);
  const ovC     = scoreToColor(overall);

  const CTRL_BOXES = [
    { label: '16 Policy Domains + Lender Covenants', cx: 255, phases: [0, 1] },
    { label: 'Hyperscaler SLAs + AHJ Rules',         cx: 755, phases: [3, 4] },
  ];
  const MECH_BOXES = [
    { label: 'Site Data + LBNL + EIA Grid',  cx: 110, phases: [0]    },
    { label: 'Utility Queue + Long-Lead',     cx: 315, phases: [1, 2] },
    { label: 'Pro Forma + Tenant Specs',      cx: 525, phases: [0, 1] },
    { label: 'AISentry Live Telemetry',       cx: 750, phases: [3, 4] },
  ];

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      height: 'calc(100vh - 64px)',
      background: '#080d1a', overflow: 'hidden',
    }}>
      {/* ── Mission Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 24px',
        background: '#0a0f1e',
        borderBottom: '1px solid #1e3a5f',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              fontSize: 9, color: '#38bdf8', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.12em',
            }}>
              DC SITE
            </div>
            <select
              value={site.code}
              onChange={e => {
                const next = SITES.find(s => s.code === e.target.value);
                if (next) onSiteChange(next);
              }}
              style={{
                background: '#111e33',
                border: '1px solid #1e3a5f',
                borderRadius: 8,
                padding: '6px 28px 6px 12px',
                color: '#f1f5f9',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2364748b'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 10px center',
                minWidth: 160,
              }}
            >
              <optgroup label="US Sites">
                {SITES.filter(s => s.region === 'US').map(s => (
                  <option key={s.code} value={s.code}
                          style={{ background: '#0d1829', color: '#f1f5f9' }}>
                    {s.name} {'\u00B7'} {s.iso || s.state}
                  </option>
                ))}
              </optgroup>
              <optgroup label="LATAM Sites">
                {SITES.filter(s => s.region === 'LATAM').map(s => (
                  <option key={s.code} value={s.code}
                          style={{ background: '#0d1829', color: '#f1f5f9' }}>
                    {s.name}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#f1f5f9', lineHeight: 1.2 }}>
              Aligned {site.name}
            </div>
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
              {site.city} {'\u00B7'} {iso} {'\u00B7'} IDEF0 Owner{'\u2019'}s Project Intelligence
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {['queue_delay', 'site_risk', 'procurement_risk'].map(key => {
            const row = siteC4 && siteC4[key];
            const c = scoreToColor(row ? row.score : null);
            const lbl = { queue_delay: 'Queue', site_risk: 'Site', procurement_risk: 'Proc' }[key];
            return (
              <div key={key} style={{
                background: '#0d1829', border: `1px solid ${c.dot}45`,
                borderRadius: 10, padding: '6px 12px',
                textAlign: 'center', minWidth: 54,
              }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: c.dot }}>
                  {row ? Math.round(row.score) : '\u2014'}
                </div>
                <div style={{ fontSize: 7.5, color: '#475569', fontWeight: 700, marginTop: 1 }}>
                  {lbl}
                </div>
              </div>
            );
          })}
          <div style={{
            background: '#0d1829',
            border: `2px solid ${ovC.dot}`,
            borderRadius: 10, padding: '6px 16px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: ovC.dot, lineHeight: 1 }}>
              {overall !== null ? Math.round(overall) : '\u2014'}
            </div>
            <div style={{ fontSize: 7.5, color: '#475569', fontWeight: 700, marginTop: 2 }}>
              SITE RISK
            </div>
          </div>
        </div>
      </div>

      {/* ── Main Canvas ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* SVG structural diagram */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <svg width="100%" height="100%"
               viewBox={`0 0 1000 ${SVG_H}`}
               preserveAspectRatio="xMidYMid meet"
               style={{ display: 'block' }}>

            <defs>
              {[0, 1, 2, 3].map(i => {
                const c = scoreCol(scores[i]);
                return (
                  <marker key={i} id={`v2a${i}`}
                          markerWidth="6" markerHeight="6"
                          refX="5" refY="3" orient="auto">
                    <path d="M0 0 L6 3 L0 6 Z" fill={c} />
                  </marker>
                );
              })}
            </defs>

            {/* ── Controls rail ── */}
            <rect x={0} y={0} width={1000} height={CTRL_H}
                  fill="#0a1428" />
            <line x1={0} y1={CTRL_H} x2={1000} y2={CTRL_H}
                  stroke="#1e3a5f" strokeWidth="1" />
            <text x={14} y={18} fill="#38bdf8" fontSize="8" fontWeight="700">
              CONTROLS
            </text>

            {CTRL_BOXES.map((cb, ci) => {
              const bw = cb.label.length * 5.1 + 22;
              const bx = cb.cx - bw / 2;
              return (
                <g key={ci}>
                  <rect x={bx} y={24} width={bw} height={22} rx={5}
                        fill="#111e33" stroke="#2d4a6e" strokeWidth="1" />
                  <text x={cb.cx} y={39} textAnchor="middle"
                        fill="#94a3b8" fontSize="8">{cb.label}</text>
                  {cb.phases.map(pi => {
                    const px = PL[pi].cx;
                    return (
                      <path key={pi}
                            d={`M${cb.cx} 46 C${cb.cx} 68 ${px} 68 ${px} ${PH_Y}`}
                            fill="none" stroke="#2d4a6e"
                            strokeWidth="1" strokeDasharray="3,2" />
                    );
                  })}
                </g>
              );
            })}

            {/* ── Phase cards ── */}
            {PHASES.map((phase, i) => (
              <V2PhaseCard
                key={phase.id}
                phase={phase}
                pl={PL[i]}
                score={scores[i]}
                colors={scoreToColor(scores[i])}
                onClick={() => onPhaseClick(phase)}
              />
            ))}

            {/* ── Inter-phase flow arrows ── */}
            {[0, 1, 2, 3].map(i => {
              const x1 = PL[i].x + PH_W + 1;
              const x2 = PL[i + 1].x - 1;
              const fy = PH_Y + PH_H / 2;
              const c  = scoreCol(scores[i]);
              return (
                <path key={i}
                      d={`M${x1} ${fy} L${x2} ${fy}`}
                      fill="none" stroke={c} strokeWidth="2"
                      markerEnd={`url(#v2a${i})`}
                      opacity="0.9" />
              );
            })}

            {/* ── Timeline spine ── */}
            <V2Timeline scores={scores} />

            {/* ── Mechanisms rail ── */}
            <rect x={0} y={MECH_Y} width={1000} height={MECH_H + 14}
                  fill="#0a1428" />
            <line x1={0} y1={MECH_Y} x2={1000} y2={MECH_Y}
                  stroke="#1e3a5f" strokeWidth="1" />
            <text x={14} y={MECH_Y + 16} fill="#10b981" fontSize="8" fontWeight="700">
              MECHANISMS
            </text>

            {MECH_BOXES.map((mb, mi) => {
              const bw = mb.label.length * 5.0 + 18;
              const bx = mb.cx - bw / 2;
              const mcy = MECH_Y + 40;
              return (
                <g key={mi}>
                  <rect x={bx} y={mcy - 11} width={bw} height={22} rx={5}
                        fill="#0a1e14" stroke="#1a3a2a" strokeWidth="1" />
                  <text x={mb.cx} y={mcy + 5} textAnchor="middle"
                        fill="#6ee7b7" fontSize="7.5">{mb.label}</text>
                  {mb.phases.map(pi => {
                    const px = PL[pi].cx;
                    return (
                      <path key={pi}
                            d={`M${mb.cx} ${mcy - 11} C${mb.cx} ${MECH_Y - 4} ${px} ${MECH_Y - 4} ${px} ${TLINE_Y + TLINE_H}`}
                            fill="none" stroke="#1a3a2a"
                            strokeWidth="1" strokeDasharray="3,2" />
                    );
                  })}
                </g>
              );
            })}

            {/* Watermark */}
            <text x={990} y={SVG_H - 5} textAnchor="end"
                  fill="#1e3a5f" fontSize="6.5" fontWeight="500">
              AXIOM {'\u00B7'} IDEF0 {'\u00B7'} CORR-004
            </text>
          </svg>
        </div>

        {/* ── Intelligence rail ── */}
        <V2IntelRail site={site} siteC4={siteC4} scores={scores} />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────

const ProjectOracleV2 = () => {
  const [corr004,        setCorr004]        = useState({});
  const [loading,        setLoading]        = useState(true);
  const [selectedSite,   setSelectedSite]   = useState(SITES[1]); // Ashburn default
  const [selectedPhase,  setSelectedPhase]  = useState(null);
  const [phaseScore,     setPhaseScore]     = useState(null);
  const [view,           setView]           = useState('site');
  const [drillPhaseId,   setDrillPhaseId]   = useState(null);


  const fetchCorr004 = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/ingest/corr004/results`);
      if (!res.ok) return;
      const data = await res.json();
      const indexed = {};
      for (const row of data.results || []) {
        if (!indexed[row.edge_node_id]) indexed[row.edge_node_id] = {};
        indexed[row.edge_node_id][row.correlator] = row;
      }
      setCorr004(indexed);
    } catch (e) {
      console.warn('[ProjectOracle] CORR-004 fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCorr004(); }, [fetchCorr004]);

  // Deep-link from DC Fleet: if localStorage has a site code, open that site directly
  useEffect(() => {
    try {
      const code = localStorage.getItem('projectOracleSelectedSite');
      if (code) {
        localStorage.removeItem('projectOracleSelectedSite');
        const match = SITES.find(s => s.code === code);
        if (match) {
          setSelectedSite(match);
          setSelectedPhase(null);
          setView('site');
        }
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBackToPortfolio = () => {
    setView('site');
  };

  // Layer 3: Phase Activity Drilldown
  if (view === 'phase') {
    return (
      <PhaseOracle
        site={selectedSite}
        initialPhaseId={drillPhaseId}
        siteCorr004={corr004[selectedSite?.code] || null}
        onBack={(dest) => {
          setView('site');
          setDrillPhaseId(null);
        }}
      />
    );
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                  height: 'calc(100vh - 64px)', background: '#fff' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, border: '4px solid #e0f2fe',
                      borderTopColor: '#0ea5e9', borderRadius: '50%',
                      animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        <div style={{ color: '#0ea5e9', fontWeight: 600 }}>Loading Project Flow...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );

  return (
    <SiteFlowV2
      site={selectedSite}
      corr004={corr004}
      onPhaseClick={(phase) => {
        setDrillPhaseId(phase.id);
        setView('phase');
      }}
      onBack={handleBackToPortfolio}
      onSiteChange={(next) => {
        setSelectedSite(next);
        setSelectedPhase(null);
        setView('site');
      }}
    />
  );
};

export default ProjectOracleV2;
